/**
 * Killing.
 *
 * Kate: "You press that and you can bite onto something. And if you bite for 5 seconds,
 * you kill someone."
 *
 * So a kill is a five-second *continuous* hold on one target. Letting go, or the target
 * breaking away, resets the bite — it does not bank progress. Damage still accrues
 * meanwhile according to the biting animal's strength, because the animal is the weapon.
 */
import { type Species } from './species'

/** "If you bite for 5 seconds, you kill someone." */
export const KILL_SECONDS = 5
/** How close you must be to keep a bite latched, in metres. */
export const BITE_RANGE = 2.4

export interface BiteTarget {
  id: string
  x: number
  z: number
  /** Species of the target — the cat/bird rule reads this. */
  speciesId: string
  applyDamage(amount: number): void
  kill(): void
  isDead(): boolean
}

export class Bite {
  /** The target currently latched onto, or null. */
  targetId: string | null = null
  /** Seconds of unbroken hold on the current target. */
  heldFor = 0

  /**
   * Advance one fixed step.
   * Returns true on the frame the target dies.
   */
  update(dt: number, attacking: boolean, attacker: Species, target: BiteTarget | null): boolean {
    if (!attacking || !target || target.isDead()) {
      this.reset()
      return false
    }

    const inRange = Math.hypot(target.x - this.lastX, target.z - this.lastZ) <= BITE_RANGE

    // A different target, or the old one slipping out of range, breaks the hold.
    if (this.targetId !== target.id || !inRange) {
      this.targetId = target.id
      this.heldFor = 0
    }

    this.heldFor += dt
    target.applyDamage(attacker.strength * dt)

    if (this.heldFor >= KILL_SECONDS) {
      target.kill()
      this.reset()
      return true
    }
    return false
  }

  /** Where the attacker is, so range can be measured. Set before `update`. */
  private lastX = 0
  private lastZ = 0

  setAttackerPosition(x: number, z: number): void {
    this.lastX = x
    this.lastZ = z
  }

  reset(): void {
    this.targetId = null
    this.heldFor = 0
  }

  /** 0..1 progress toward the kill, for a HUD ring. */
  get progress(): number {
    return Math.min(1, this.heldFor / KILL_SECONDS)
  }
}
